import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../context/ConfirmContext';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import LabelProposalsPanel from '../components/LabelProposalsPanel';
import FilterRowEditor from '../components/FilterRowEditor';
import Button from '../components/ui/Button';
import FieldSelect from '../components/ui/FieldSelect';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';
import { resolveReturnFocus } from '../components/returnFocus';
import {
  derivedBaseId, derivedRowErrorMessage, derivedRowNote, isDerivedSource,
} from '../utils/derivedCatalogRow';

interface FilterEntry {
  id: number;
  brand: string;
  brand_canon: string | null;
  model: string;
  manufacturer: string | null;
  maedb_model_number: string | null;
  depth_inches: number;
  nominal_size: string;
  actual_size: string | null;
  dp_curve: [number, number][] | null;
  merv: number | null;
  mpr: number | null;
  fpr: number | null;
  merv_equivalent: number;
  performance_tier: string | null;
  pse_0_3_to_1_0: string | null;
  pse_1_0_to_3_0: string | null;
  pse_3_0_to_10: string | null;
  dp_at_300fpm: number | null;
  max_airflow_cfm: number | null;
  dust_hold_g: number | null;
  title20_listed: boolean;
  filter_life_months: number | null;
  notes: string | null;
  dp_at_400cfm: number | null;
  dp_at_800cfm: number | null;
  dp_at_1200cfm: number | null;
  dp_at_1600cfm: number | null;
  dp_at_max_cfm: number | null;
  max_rated_cfm: number | null;
  c_coefficient: number | null;
  source: string | null;
  source_date: string | null;
  source_meta: Record<string, unknown> | null;
}

// Provenance badge. maedb/filter_label = certified; certified_scaled =
// certified too (FS-686: a certified curve scaled to this size by face area),
// so it sits in the green family but in lime, to tell it apart from a real
// listing at a glance; manual/extrapolated/estimated = approximate;
// randy = identity-only.
const SRC_BADGE: Record<string, string> = {
  maedb: 'border border-status-success-border bg-status-success text-status-success-foreground',
  filter_label: 'border border-status-success-border bg-status-success text-status-success-foreground',
  certified_scaled: 'border border-status-scaled-border bg-status-scaled text-status-scaled-foreground',
  manufacturer: 'border border-status-manufacturer-border bg-status-manufacturer text-status-manufacturer-foreground',
  manual: 'border border-status-warning-border bg-status-warning text-status-warning-foreground',
  extrapolated: 'border border-status-warning-border bg-status-warning text-status-warning-foreground',
  estimated: 'border border-status-warning-border bg-status-warning text-status-warning-foreground',
  randy: 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground',
};

type SortField = 'id' | 'brand' | 'model' | 'merv_equivalent' | 'depth_inches' | 'nominal_size' | 'dp_at_300fpm' | 'source';
type SortDir = 'asc' | 'desc';

const dpColor = (val: number | null): string => {
  if (val === null) return '';
  if (val < 0.10) return 'border border-status-success-border bg-status-success text-status-success-foreground';
  if (val < 0.20) return 'border border-status-warning-border bg-status-warning text-status-warning-foreground';
  if (val < 0.30) return 'border border-status-warning-border bg-status-warning-strong text-status-warning-foreground';
  return 'border border-status-error-border bg-status-error text-status-error-foreground';
};

/** FS-511 audit entry, as GET /:id/history returns them (newest first). */
interface HistoryEntry {
  id: number;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  actor: string | null;
  origin: string | null;
  changed_at: string;
  old_row: Record<string, unknown> | null;
  new_row: Record<string, unknown> | null;
  proposal_id: number | null;
}

// Fields worth diffing in the history view; everything else is derived or
// noise. dp_curve is summarized as a point count.
const HISTORY_FIELDS = [
  'brand', 'model', 'manufacturer', 'nominal_size', 'depth_inches', 'merv',
  'merv_equivalent', 'maedb_model_number', 'source', 'source_date',
  'dp_curve', 'dp_at_300fpm', 'filter_life_months', 'title20_listed',
  'performance_tier', 'notes',
];

const historyValue = (field: string, row: Record<string, unknown> | null): string => {
  if (!row || row[field] == null) return '—';
  if (field === 'dp_curve') {
    const curve = row[field];
    return Array.isArray(curve) ? `${curve.length} points` : '—';
  }
  return String(row[field]);
};

/** 'maedb_reload:<ts>-<sha8>' origins collapse to a labeled badge; the full
 * run id stays discoverable via the title attribute. */
const originBadge = (origin: string | null) => {
  if (!origin) return { label: 'sql', title: 'direct SQL (no origin recorded)' };
  if (origin.startsWith('maedb_reload:')) return { label: 'maedb reload', title: origin };
  if (origin.startsWith('randy_import:')) return { label: 'randy import', title: origin };
  if (origin.startsWith('maedb_extrapolate:')) return { label: 'extrapolate', title: origin };
  // FS-686: the nightly sweep that creates, updates and retires scaled rows.
  if (origin.startsWith('certified_scaled:')) return { label: 'scaled rows', title: origin };
  return { label: origin, title: origin };
};

/**
 * FS-686: the provenance of a scaled (or legacy extrapolated) row, as the
 * sentences shown in the detail's "Source detail" line. Everything is read
 * from source_meta; a field the meta lacks is left out rather than guessed.
 */
const scaledSummary = (m: Record<string, unknown>): string[] => {
  const out: string[] = [];
  const base = derivedBaseId(m);
  const nominal = typeof m.base_nominal === 'string' && m.base_nominal.trim() !== ''
    ? m.base_nominal.trim() : null;
  const ratio = typeof m.area_ratio === 'number' && Number.isFinite(m.area_ratio)
    ? String(Number(m.area_ratio.toFixed(4))) : null;
  if (base != null || nominal) {
    let line = `Scaled from ${base != null ? `entry #${base}` : 'a certified entry'}`;
    if (nominal) line += ` (${nominal})`;
    if (ratio) line += `, area ratio ${ratio}`;
    out.push(line);
  }
  const ids = Array.isArray(m.line_ids)
    ? m.line_ids.filter(v => v != null && String(v).trim() !== '').map(v => `#${v}`) : [];
  if (ids.length > 0) out.push(`product line: entries ${ids.join(', ')}`);
  const numbers = Array.isArray(m.line_numbers)
    ? m.line_numbers.filter(v => v != null && String(v).trim() !== '').map(String) : [];
  if (numbers.length > 0) out.push(`MAEDbS numbers ${numbers.join(', ')}`);
  return out;
};

const FilterHistorySection: React.FC<{ baseUrl: string; filterId: number }> = ({
  baseUrl, filterId,
}) => {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setFailed(false);
    axiosInstance.get(`${baseUrl}/${filterId}/history?limit=50`)
      // Server wraps in { catalog_id, limit, data } (house list-envelope convention).
      .then(res => { if (!cancelled) setEntries(Array.isArray(res.data) ? res.data : res.data?.data ?? []); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [baseUrl, filterId]);

  if (failed) return null; // endpoint not deployed yet — stay quiet
  return (
    <div className="mt-4">
      <h4 className="text-sm font-bold mb-2">Change History</h4>
      {entries === null && <div className="text-gray-600 text-sm">Loading…</div>}
      {entries !== null && entries.length === 0 && (
        <div className="text-gray-600 text-sm">No changes recorded.</div>
      )}
      {entries !== null && entries.length > 0 && (
        <div className="space-y-2">
          {entries.map(e => {
            const badge = originBadge(e.origin);
            const changed = e.action === 'UPDATE'
              ? HISTORY_FIELDS.filter(f =>
                historyValue(f, e.old_row) !== historyValue(f, e.new_row))
              : [];
            return (
              <div key={e.id} className="border border-border rounded-sm p-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">
                    {new Date(e.changed_at).toLocaleString()}
                  </span>
                  <span className="font-medium">{e.actor ?? 'unknown'}</span>
                  <span title={badge.title}
                    className="px-1.5 py-0.5 rounded-sm text-[10px] border border-status-neutral-border bg-status-neutral text-status-neutral-foreground">
                    {badge.label}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${
                    e.action === 'DELETE' ? 'border border-status-error-border bg-status-error text-status-error-foreground'
                      : e.action === 'INSERT' ? 'border border-status-success-border bg-status-success text-status-success-foreground'
                        : 'border border-status-info-border bg-status-info text-status-info-foreground'}`}>
                    {e.action.toLowerCase()}
                  </span>
                  {e.proposal_id != null && (
                    <span className="text-gray-600">proposal #{e.proposal_id}</span>
                  )}
                </div>
                {e.action === 'INSERT' && (
                  <div className="mt-1 text-gray-600">
                    created as <span className="font-mono">{historyValue('model', e.new_row)}</span>
                    {' '}({historyValue('source', e.new_row)})
                  </div>
                )}
                {e.action === 'DELETE' && (
                  <div className="mt-1 text-gray-600">
                    deleted (<span className="font-mono">{historyValue('model', e.old_row)}</span>)
                  </div>
                )}
                {changed.length > 0 && (
                  <table className="mt-1 w-full">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-0.5 pr-3">Field</th>
                        <th className="py-0.5 pr-3">Old</th>
                        <th className="py-0.5">New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changed.map(f => (
                        <tr key={f} className="border-t border-border">
                          <td className="py-0.5 pr-3">{f}</td>
                          <td className="py-0.5 pr-3 text-gray-600">{historyValue(f, e.old_row)}</td>
                          <td className="py-0.5 font-medium">{historyValue(f, e.new_row)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {e.action === 'UPDATE' && changed.length === 0 && (
                  <div className="mt-1 text-gray-600">derived fields only</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FilterCatalogPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const confirm = useConfirm();
  const canManage = hasPermission('manage_filters');

  const [filters, setFilters] = useState<FilterEntry[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchFailed, setFetchFailed] = useState(false);

  // Filters
  const [brandFilter, setBrandFilter] = useState('');
  const [depthFilter, setDepthFilter] = useState('');
  const [mervFilter, setMervFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Sort
  const [sortField, setSortField] = useState<SortField>('brand');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Edit (unified editor — FS-504; creation stays in LabelProposalsPanel)
  const [editingRow, setEditingRow] = useState<FilterEntry | null>(null);

  // Detail view
  const [selectedFilter, setSelectedFilter] = useState<FilterEntry | null>(null);

  // FS-594: ?filter=<catalog id> opens that row's detail panel on load, so the
  // analysis page can send an operator straight to a filter's performance
  // data. Read-only: clicking a row still selects locally, as before.
  const [searchParams] = useSearchParams();
  const deepLinkId = searchParams.get('filter');
  const [deepLinkError, setDeepLinkError] = useState('');
  const detailRef = useRef<HTMLDivElement | null>(null);

  const baseUrl = config.filterCatalogApiUrl ?? '';

  const fetchBrands = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${baseUrl}/brands`);
      setBrands(res.data);
    } catch { /* ignore */ }
  }, [baseUrl]);

  const fetchSources = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${baseUrl}/sources`);
      setSources(res.data);
    } catch { /* ignore */ }
  }, [baseUrl]);

  const fetchFilters = useCallback(async () => {
    setLoading(true);
    setError('');
    setFetchFailed(false);
    try {
      const params = new URLSearchParams();
      if (brandFilter) params.set('brand', brandFilter);
      if (depthFilter) params.set('depth', depthFilter);
      if (mervFilter) params.set('merv', mervFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      if (searchFilter) params.set('search', searchFilter);
      params.set('sort', sortField);
      params.set('dir', sortDir);
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));

      const res = await axiosInstance.get(`${baseUrl}?${params.toString()}`);
      setFilters(res.data.data);
      setTotal(res.data.total);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError?.response?.data?.error ?? 'Failed to fetch filters');
      setFetchFailed(true);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, brandFilter, depthFilter, mervFilter, sourceFilter,
      searchFilter, page, sortField, sortDir]);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);
  useEffect(() => { fetchSources(); }, [fetchSources]);
  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  // FS-594: fetch the deep-linked row by id rather than hunting for it in the
  // current page — the list is paginated and sorted server-side, so the target
  // is usually not among the 50 rows on screen. Runs only when the id in the
  // URL changes, so closing the panel does not reopen it.
  useEffect(() => {
    if (!deepLinkId) {
      setDeepLinkError('');
      return;
    }
    const id = Number(deepLinkId);
    if (!Number.isInteger(id) || id <= 0) {
      setDeepLinkError(`"${deepLinkId}" is not a filter id.`);
      return;
    }
    let cancelled = false;
    setDeepLinkError('');
    axiosInstance.get(`${baseUrl}/${id}`)
      .then(res => {
        if (cancelled) return;
        if (res.data?.id != null) {
          setSelectedFilter(res.data);
        } else {
          setDeepLinkError(`Filter #${id} is not in the catalog.`);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDeepLinkError(`Filter #${id} could not be loaded — it may have been removed from the catalog.`);
        }
      });
    return () => { cancelled = true; };
  }, [baseUrl, deepLinkId]);

  // FS-594 follow-up: the panel renders BELOW a full page of table rows, so a
  // deep-link arrival left the operator at the top of the table with no sign
  // anything was selected.
  //
  // The scroll this replaces ran from the by-id fetch callback, one
  // requestAnimationFrame after the panel was selected. It fired — but far too
  // early to be worth anything. The single-row by-id lookup beats the sorted
  // 50-row list query, so at that moment the table is still its "Loading..."
  // placeholder, the page is barely a screen tall and the panel is already in
  // view: the scroll is a no-op. The rows then land ABOVE the panel and shove
  // it back under the fold, with the viewport still at the top. Hence the
  // `loading` gate — the page has to be settled before its geometry means
  // anything.
  //
  // Fires once per deep-linked id: the ref below records the id already
  // scrolled to, so unrelated re-renders, a later row click, a re-query from a
  // filter change, or closing and reopening the editor cannot scroll again. A
  // row click never reaches this — its selection id does not match the id in
  // the URL (and on a plain /filter-catalog visit there is no id at all).
  const scrolledDeepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkId) {
      scrolledDeepLinkRef.current = null;
      return;
    }
    if (scrolledDeepLinkRef.current === deepLinkId) return;
    if (loading) return;
    // The panel is unmounted while the row editor is open.
    if (editingRow !== null) return;
    if (!selectedFilter || String(selectedFilter.id) !== deepLinkId) return;
    const el = detailRef.current;
    if (!el) return;
    scrolledDeepLinkRef.current = deepLinkId;
    // 'nearest' moves the page the least amount that brings the panel on
    // screen, so the bottom of the table stays visible above it and the
    // operator can see where they came from; 'smooth' makes the movement
    // legible as a scroll rather than looking like a different page loaded.
    // Optional-called: jsdom implements no layout and defines no
    // scrollIntoView.
    el.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [deepLinkId, selectedFilter, editingRow, loading]);

  // Sort is server-side (4k+ rows: a client sort of the 50-row page is
  // misleading). Render the rows as the API returned them.
  const sorted = filters;

  const toggleSort = (field: SortField) => {
    setPage(0);  // sort changes the full ordering -> re-query from page 1
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' \u25b2' : ' \u25bc') : '';

  const sortHeader = (field: SortField, label: string) => (
    <th className="px-2 py-1.5 border">
      <Button variant="link" size="sm" className="p-0 text-xs"
        onClick={() => toggleSort(field)} title={`Sort catalog entries by ${label}`}>
        {label}{sortIndicator(field)}
      </Button>
    </th>
  );

  const focusAfterClose = () => requestAnimationFrame(() => resolveReturnFocus()?.focus());

  const openEdit = (f: FilterEntry) => {
    setEditingRow(f);
    setSelectedFilter(null);
  };

  const handleDelete = async (f: FilterEntry) => {
    const ok = await confirm({
      title: 'Delete filter entry?',
      message: 'This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await axiosInstance.delete(`${baseUrl}/${f.id}`);
      fetchFilters();
    } catch (err: unknown) {
      // The button is disabled on derived rows, but a row listed before the
      // sweep turned it into one can still get here; the API answers 409.
      const derived = derivedRowErrorMessage(err, f.source_meta);
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(derived ?? axiosError?.response?.data?.message ?? 'Failed to delete');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const ratingDisplay = (f: FilterEntry) => {
    if (f.mpr !== null) return `MPR ${f.mpr}`;
    if (f.fpr !== null) return `FPR ${f.fpr}`;
    return `MERV ${f.merv_equivalent}`;
  };

  return (
    <div className="h-full w-full bg-gray-50">
      <div className="max-w-7xl mx-auto p-2">
        {canManage && (
          <LabelProposalsPanel
            baseUrl={baseUrl}
            onCatalogChanged={() => { fetchFilters(); fetchSources(); }}
          />
        )}
        <div className="bg-card border border-border rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Filter Catalog</h2>
            <span className="text-sm text-muted-foreground">{total} filters</span>
          </div>

          {error && (
            <div className="border border-status-error-border bg-status-error text-status-error-foreground p-3 rounded-md mb-4" role="alert">{error}</div>
          )}

          {deepLinkError && (
            <div className="border border-status-warning-border bg-status-warning text-status-warning-foreground p-3 rounded-md mb-4">{deepLinkError}</div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 items-start">
            <FieldSelect id="filters-brand" label="Brand" help="Show only filters from this brand."
              value={brandFilter} onChange={next => { setBrandFilter(next); setPage(0); }}
              options={[{ value: '', label: 'All Brands' }, ...brands.map(b => ({ value: b, label: b }))]} />
            <FieldSelect id="filters-depth" label="Depth" help="Show only filters of this depth."
              value={depthFilter} onChange={next => { setDepthFilter(next); setPage(0); }}
              options={[{ value: '', label: 'All Depths' }, ...['1', '2', '3', '4', '5', '6'].map(d => ({ value: d, label: `${d}\"` }))]} />
            <FieldSelect id="filters-merv" label="MERV" help="Show only filters with this MERV rating."
              value={mervFilter} onChange={next => { setMervFilter(next); setPage(0); }}
              options={[{ value: '', label: 'All MERV' }, ...Array.from({ length: 16 }, (_, i) => ({ value: String(i + 1), label: `MERV ${i + 1}` }))]} />
            <FieldSelect id="filters-source" label="Source" help="Show only filters from this catalog source."
              value={sourceFilter} onChange={next => { setSourceFilter(next); setPage(0); }}
              options={[{ value: '', label: 'All Sources' }, ...sources.map(s => ({ value: s, label: s }))]} />
            <div>
              <Label htmlFor="filters-search">Search</Label>
              <Input id="filters-search" type="text" placeholder="Search brand, model, notes..."
              value={searchFilter}
              onChange={e => { setSearchFilter(e.target.value); setPage(0); }}
              aria-describedby="filters-search-help" className="w-64" />
              <p id="filters-search-help" className="text-xs text-muted-foreground">Match brand, model or notes.</p>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-muted-foreground py-8 text-center">Loading...</div>
          ) : fetchFailed ? null : (
            <div className="overflow-x-auto">
              <table className="table-auto w-full text-left border-collapse border border-border text-xs">
                <thead>
                  <tr className="bg-gray-200">
                    {/* The row id is how every other surface names a catalog
                        entry — the review panel's "Update entry #7505", an
                        scaled child's SCALED-<id>-<size> model, the
                        ?filter=<id> deep link — so it is shown, sortable and
                        searchable rather than left to be guessed. */}
                    {sortHeader('id', 'ID')}
                    {sortHeader('brand', 'Brand')}
                    {sortHeader('model', 'Model')}
                    {sortHeader('depth_inches', 'Depth')}
                    {sortHeader('nominal_size', 'Size')}
                    <th className="px-2 py-1.5 border">Rating</th>
                    {sortHeader('merv_equivalent', 'MERV Eq.')}
                    {sortHeader('dp_at_300fpm', '\u0394P @300fpm')}
                    <th className="px-2 py-1.5 border">Life (mo)</th>
                    <th className="px-2 py-1.5 border">T20</th>
                    {sortHeader('source', 'Source')}
                    {canManage && <th className="px-2 py-1.5 border">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(f => (
                    <tr key={f.id} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedFilter(f)}>
                      <td className="px-2 py-1 border font-mono text-muted-foreground">{f.id}</td>
                      <td className="px-2 py-1 border">{f.brand}</td>
                      <td className="px-2 py-1 border font-mono">
                        <Button variant="link" size="sm" className="p-0 text-xs text-left"
                          onClick={event => { event.stopPropagation(); setSelectedFilter(f); }}
                          title={`Read filter catalog entry #${f.id} details`}>{f.model}</Button>
                      </td>
                      <td className="px-2 py-1 border text-center">{f.depth_inches}"</td>
                      <td className="px-2 py-1 border">{f.nominal_size}</td>
                      <td className="px-2 py-1 border">{ratingDisplay(f)}</td>
                      <td className="px-2 py-1 border text-center">{f.merv_equivalent}</td>
                      <td className={`px-2 py-1 border text-center ${dpColor(f.dp_at_300fpm)}`}>
                        {f.dp_at_300fpm?.toFixed(2) ?? '-'}
                      </td>
                      <td className="px-2 py-1 border text-center">{f.filter_life_months ?? '-'}</td>
                      <td className="px-2 py-1 border text-center">{f.title20_listed ? 'Yes' : ''}</td>
                      <td className="px-2 py-1 border text-center">
                        {f.source && (
                          <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${SRC_BADGE[f.source] ?? 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground'}`}>
                            {f.source}
                          </span>
                        )}
                      </td>
                      {canManage && (
                        <td className="px-2 py-1 border">
                          {isDerivedSource(f.source) ? (
                            // FS-686: derived rows are rebuilt from their base
                            // every night; the API refuses edits and deletes.
                            <div className="flex gap-1 items-center" onClick={e => e.stopPropagation()}>
                              <Button variant="secondary" size="sm" disabled title={derivedRowNote(f.source_meta)}>
                                Edit
                              </Button>
                              <Button variant="danger" size="sm" disabled title={derivedRowNote(f.source_meta)}>
                                Del
                              </Button>
                              <span className="text-[10px] text-muted-foreground" title={derivedRowNote(f.source_meta)}>
                                {derivedBaseId(f.source_meta) != null
                                  ? `derived from #${derivedBaseId(f.source_meta)}`
                                  : 'derived'}
                              </span>
                            </div>
                          ) : (
                            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                              <Button variant="primary" size="sm" onClick={() => openEdit(f)}
                                title="Change this entry's identity, size or pressure-drop curve. Devices matched to it use the new curve for their airflow.">
                                Edit
                              </Button>
                              <Button variant="danger" size="sm" onClick={() => handleDelete(f)}
                                title="Delete this entry from the filter catalog. A confirmation opens first.">
                                Del
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={canManage ? 12 : 11} className="px-2 py-4 border text-center text-gray-600">
                        No filters found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <Button variant="secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}
                title="Show the previous page of catalog entries.">
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button variant="secondary" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                title="Show the next page of catalog entries.">
                Next
              </Button>
            </div>
          )}

          {/* Pressure drop color key */}
          <div className="mt-4 pt-3 border-t border-border">
            <div className="text-xs text-gray-600 mb-2 font-semibold">
              Pressure Drop (in. w.c.) — filter resistance to airflow
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded-sm border border-status-success-border bg-status-success text-status-success-foreground">
                &lt; 0.10 &nbsp;Low — minimal impact on HVAC
              </span>
              <span className="px-2 py-1 rounded-sm border border-status-warning-border bg-status-warning text-status-warning-foreground">
                0.10–0.19 &nbsp;Moderate — acceptable
              </span>
              <span className="px-2 py-1 rounded-sm border border-status-warning-border bg-status-warning-strong text-status-warning-foreground">
                0.20–0.29 &nbsp;High — reduces airflow
              </span>
              <span className="px-2 py-1 rounded-sm border border-status-error-border bg-status-error text-status-error-foreground">
                {'\u2265'} 0.30 &nbsp;Very high — strains blower
              </span>
            </div>
          </div>
        </div>

        {/* Detail Panel */}
        {selectedFilter && editingRow === null && (
          <div ref={detailRef} data-testid="filter-detail"
            className="bg-card border border-border rounded-lg shadow-md p-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">
                {selectedFilter.brand} &mdash; {selectedFilter.model}{' '}
                <span className="font-mono text-sm font-normal text-muted-foreground">
                  #{selectedFilter.id}
                </span>
              </h3>
              <Button variant="secondary" onClick={() => { setSelectedFilter(null); focusAfterClose(); }}
                title="Close this filter's details. The catalog list stays where it is.">
                Close
              </Button>

            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-muted-foreground">Size:</span> {selectedFilter.nominal_size}</div>
              <div><span className="text-muted-foreground">Actual:</span> {selectedFilter.actual_size ?? '-'}</div>
              <div><span className="text-muted-foreground">Depth:</span> {selectedFilter.depth_inches}"</div>
              <div><span className="text-muted-foreground">MERV:</span> {selectedFilter.merv ?? '-'}</div>
              <div><span className="text-muted-foreground">MPR:</span> {selectedFilter.mpr ?? '-'}</div>
              <div><span className="text-muted-foreground">FPR:</span> {selectedFilter.fpr ?? '-'}</div>
              <div><span className="text-muted-foreground">MERV Equiv:</span> {selectedFilter.merv_equivalent}</div>
              <div><span className="text-muted-foreground">Tier:</span> {selectedFilter.performance_tier ?? '-'}</div>
              <div><span className="text-muted-foreground">Life:</span> {selectedFilter.filter_life_months ?? '-'} mo</div>
              <div><span className="text-muted-foreground">Dust Hold:</span> {selectedFilter.dust_hold_g ?? '-'} g</div>
              <div><span className="text-muted-foreground">Max CFM:</span> {selectedFilter.max_airflow_cfm ?? '-'}</div>
              <div><span className="text-muted-foreground">Title 20:</span> {selectedFilter.title20_listed ? 'Yes' : 'No'}</div>
              <div>
                <span className="text-muted-foreground">Source:</span>{' '}
                {selectedFilter.source
                  ? <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${SRC_BADGE[selectedFilter.source] ?? 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground'}`}>
                      {selectedFilter.source}
                    </span>
                  : '-'}
                {selectedFilter.source_date ? ` (${selectedFilter.source_date})` : ''}
              </div>
              {selectedFilter.source_meta && (() => {
                // Curated summary — never a raw dump. Nested snapshots
                // (prior_catalog_row, submitted_identity, photo lists) are
                // historical provenance that can carry retired machine names
                // (review finding 2026-08-20 round 3); the full before/after
                // images live in the Change History section below.
                const m = selectedFilter.source_meta as Record<string, unknown>;
                const pairs: Array<[string, string]> = [];
                const put = (label: string, v: unknown) => {
                  if (v != null && String(v).trim() !== '') pairs.push([label, String(v)]);
                };
                put('origin', m.origin);
                put('reviewer', m.reviewer);
                put('proposal', m.proposal_id != null ? `#${m.proposal_id}` : null);
                put('reviewed model', m.reviewed_model);
                put('display brand', m.display_brand);
                put('resolution', (m.conflict_resolution as { action?: string } | null)?.action);
                put('review note', m.review_note);
                put('submitter note', m.submitter_note);
                if (m.prior_catalog_row) {
                  pairs.push(['prior snapshot', 'captured — see Change History']);
                }
                // FS-686: a derived row names the certified row it was scaled
                // from by entry id, the same id the review panel and the table
                // use, plus the other listings that share its curve.
                const scaled = isDerivedSource(selectedFilter.source)
                  ? scaledSummary(m) : [];
                if (pairs.length === 0 && scaled.length === 0) return null;
                return (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Source detail:</span>{' '}
                    <span className="text-[11px]">
                      {[...scaled, ...pairs.map(([k, v]) => `${k}: ${v}`)].join(' · ')}
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* Pressure drop table */}
            <h4 className="text-sm font-bold mt-4 mb-2">Pressure Drop vs CFM (in. w.c.)</h4>
            <div className="overflow-x-auto">
              <table className="table-auto text-xs border-collapse border border-border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-1 border">@300 fpm</th>
                    <th className="px-3 py-1 border">@400 CFM</th>
                    <th className="px-3 py-1 border">@800 CFM</th>
                    <th className="px-3 py-1 border">@1200 CFM</th>
                    <th className="px-3 py-1 border">@1600 CFM</th>
                    <th className="px-3 py-1 border">@Max CFM</th>
                    <th className="px-3 py-1 border">Max Rated CFM</th>
                    <th className="px-3 py-1 border">C coeff</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={`px-3 py-1 border text-center ${dpColor(selectedFilter.dp_at_300fpm)}`}>
                      {selectedFilter.dp_at_300fpm?.toFixed(3) ?? '-'}
                    </td>
                    <td className={`px-3 py-1 border text-center ${dpColor(selectedFilter.dp_at_400cfm)}`}>
                      {selectedFilter.dp_at_400cfm?.toFixed(3) ?? '-'}
                    </td>
                    <td className={`px-3 py-1 border text-center ${dpColor(selectedFilter.dp_at_800cfm)}`}>
                      {selectedFilter.dp_at_800cfm?.toFixed(3) ?? '-'}
                    </td>
                    <td className={`px-3 py-1 border text-center ${dpColor(selectedFilter.dp_at_1200cfm)}`}>
                      {selectedFilter.dp_at_1200cfm?.toFixed(3) ?? '-'}
                    </td>
                    <td className={`px-3 py-1 border text-center ${dpColor(selectedFilter.dp_at_1600cfm)}`}>
                      {selectedFilter.dp_at_1600cfm?.toFixed(3) ?? '-'}
                    </td>
                    <td className={`px-3 py-1 border text-center ${dpColor(selectedFilter.dp_at_max_cfm)}`}>
                      {selectedFilter.dp_at_max_cfm?.toFixed(3) ?? '-'}
                    </td>
                    <td className="px-3 py-1 border text-center">{selectedFilter.max_rated_cfm ?? '-'}</td>
                    <td className="px-3 py-1 border text-center">{selectedFilter.c_coefficient?.toFixed(1) ?? '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* PSE */}
            <h4 className="text-sm font-bold mt-4 mb-2">Particle Size Efficiency</h4>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><span className="text-muted-foreground">0.3-1.0{'\u00b5'}m:</span> {selectedFilter.pse_0_3_to_1_0 ?? '-'}</div>
              <div><span className="text-muted-foreground">1.0-3.0{'\u00b5'}m:</span> {selectedFilter.pse_1_0_to_3_0 ?? '-'}</div>
              <div><span className="text-muted-foreground">3.0-10{'\u00b5'}m:</span> {selectedFilter.pse_3_0_to_10 ?? '-'}</div>
            </div>

            {isDerivedSource(selectedFilter.source) && canManage && (
              <div className="mt-3 text-xs text-gray-600 bg-gray-50 border border-border rounded-sm p-2"
                data-testid="derived-row-note">
                {derivedRowNote(selectedFilter.source_meta)}.
              </div>
            )}

            {selectedFilter.notes && (
              <div className="mt-3 text-sm">
                <span className="text-muted-foreground">Notes:</span> {selectedFilter.notes}
              </div>
            )}

            {/* FS-511: per-row audit trail (manage_filters only) */}
            {canManage && (
              <FilterHistorySection baseUrl={baseUrl} filterId={selectedFilter.id} />
            )}
          </div>
        )}

        {/* Edit Form (FS-504 unified editor \u2014 identity + curve + metadata) */}
        {editingRow !== null && canManage && (
          <FilterRowEditor
            baseUrl={baseUrl}
            row={editingRow}
            onClose={() => { setEditingRow(null); focusAfterClose(); }}
            onSaved={() => { fetchFilters(); fetchBrands(); fetchSources(); }}
          />
        )}

      </div>
    </div>
  );
};

export default FilterCatalogPage;
